import { useState } from "preact/hooks"
import { Check, Loader2 } from "lucide-react"
import { LedgerAccountJson, setDefaultPaymentAccount } from "@/api/accounting.tsx"
import { AutocompleteSelect, SelectItem } from "@/accounting/autocomplete_select.tsx"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"

/**
 * Deposit accounts are bank / other-current-asset accounts (e.g. Undeposited Funds, an operating
 * bank account). Filter to those types when the accounting system populates account types;
 * otherwise show everything — Xero's chart of accounts syncs without a type, so filtering there
 * would leave an empty picker.
 *
 * The account currently flagged as the org default is always kept, so a default set before the
 * types were known can never disappear from the list that shows it.
 */
export function deposit_account_options(accounts: LedgerAccountJson[]): SelectItem[] {
  const is_deposit_type = (a: LedgerAccountJson) => {
    const t = (a.account_type ?? "").toLowerCase()
    return t.includes("bank") || t.includes("current asset")
  }
  const any_typed = accounts.some((a) => is_deposit_type(a))
  const filtered = any_typed ? accounts.filter((a) => is_deposit_type(a) || a.is_default_for_payments) : accounts
  return filtered.map((a) => ({
    id: a.id,
    name: a.expense_account,
    expense_account: a.expense_account,
    account_number: a.account_number,
  }))
}

interface DepositAccountFieldProps {
  accounts: LedgerAccountJson[]
  /** Currently selected account id (`reason_code.id`), or null while nothing is chosen. */
  value: string | null
  onChange: (accountId: string) => void
  /**
   * Called after the server has flagged `accountId` as the org's default deposit account, so the
   * caller can move `is_default_for_payments` on its own copy of the accounts.
   */
  onDefaultChange: (accountId: string) => void
  className?: string
}

/**
 * The "Deposit to account" picker, plus the control that makes the chosen account this org's
 * default for future payments. Without it the default could only be set by a system admin editing
 * the ledger account directly, so every apply started from whatever the sync happened to flag.
 */
export function DepositAccountField({
  accounts,
  value,
  onChange,
  onDefaultChange,
  className,
}: DepositAccountFieldProps) {
  const [saving, setSaving] = useState(false)
  const options = deposit_account_options(accounts)
  const selected = accounts.find((a) => a.id === value)
  const is_default = selected?.is_default_for_payments === true

  async function handle_set_default() {
    if (!selected || saving) {
      return
    }
    setSaving(true)
    const result = await setDefaultPaymentAccount(selected.id)
    setSaving(false)
    if (result.ok) {
      onDefaultChange(selected.id)
      toast({
        title: "Default deposit account saved",
        description: `${selected.expense_account} will be selected for new payments.`,
      })
    } else {
      toast({ title: "Couldn't save the default deposit account", variant: "destructive" })
    }
  }

  return (
    <div class={`space-y-1.5 ${className ?? ""}`}>
      <Label>Deposit to account</Label>
      <AutocompleteSelect
        values={options}
        initial={value ? options.find((o) => o.id === value) : undefined}
        onSelect={(item: SelectItem) => onChange(item.id)}
      />
      {selected &&
        (is_default ? (
          <div class="flex items-center gap-1 text-xs text-muted-foreground">
            <Check className="h-3 w-3" />
            Default for new payments
          </div>
        ) : (
          <Button
            type="button"
            variant="link"
            size="sm"
            class="h-auto p-0 text-xs"
            disabled={saving}
            onClick={handle_set_default}
          >
            {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            Set as default for new payments
          </Button>
        ))}
    </div>
  )
}
